import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Alert,
  AlertTitle,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useTheme,
  Divider,
  Paper,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  ExitToAppOutlined,
  PlayCircleOutline,
  HourglassEmptyOutlined,
  CheckCircleOutline,
  WarningAmberOutlined,
  RefreshOutlined,
  InfoOutlined,
  CancelOutlined,
  SendOutlined,
  LockResetOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { walletQuery, WALLET_QUERY } from "../../data/WalletService/walletGraphQL";
import Swal from "sweetalert2";
import { formatKES } from "./WalletBalance";

const ExitReactivation = ({ companyId, onStateChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [loading, setLoading] = useState(false);
  const [lifecycle, setLifecycle] = useState({
    state: "ACTIVE",
    stateSince: null,
    lastActivityAt: null,
    dormantSince: null,
    exitType: null,
    noticeGivenAt: null,
    exitEffectiveDate: null,
  });

  const [terms, setTerms] = useState({
    minRetainedBalance: 0,
    exitNoticeDays: 30,
    earlyExitFeeType: "PERCENTAGE",
    earlyExitFeeValue: 2,
    dormancyDays: 90,
    activationFee: 5000,
  });

  const [activeExitRequest, setActiveExitRequest] = useState(null);

  // Exit dialog state
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [exitMode, setExitMode] = useState("NOTICE"); // "NOTICE" or "IMMEDIATE"
  const [exitReason, setExitReason] = useState("");
  const [negotiatedRef, setNegotiatedRef] = useState("");
  const [submittingExit, setSubmittingExit] = useState(false);

  // Reactivation dialog state
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [submittingReactivation, setSubmittingReactivation] = useState(false);

  const fetchLifecycleAndTerms = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: token ? `Bearer ${token.replace(/"/g, "")}` : "",
    };

    try {
      // 1. Fetch lifecycle state and min retained balance from GraphQL wallet
      try {
        const gqlData = await walletQuery(WALLET_QUERY, { companyId: parseInt(companyId, 10) });
        if (gqlData?.wallet) {
          const w = gqlData.wallet;
          setLifecycle((prev) => ({
            ...prev,
            state: (w.lifecycleState || "ACTIVE").toUpperCase(),
            stateSince: w.updatedDate || w.createdDate,
          }));
          if (w.minimumRetainedBalance != null) {
            setTerms((prev) => ({
              ...prev,
              minRetainedBalance: Number(w.minimumRetainedBalance || 0),
            }));
          }
        }
      } catch (gqlErr) {
        console.warn("Could not query wallet lifecycle via GQL:", gqlErr);
      }


    } catch (err) {
      console.error("Failed fetching lifecycle/terms data:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchLifecycleAndTerms();
  }, [fetchLifecycleAndTerms]);

  // Handle Exit Request Submission
  const handleSubmitExit = async () => {
    if (!exitReason.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Reason Required",
        text: "Please provide a reason for the platform exit request.",
      });
      return;
    }

    setSubmittingExit(true);
    try {


      Swal.fire({
        icon: "success",
        title: "Exit Request Submitted",
        text:
          exitMode === "NOTICE"
            ? `Notice submitted. Standard ${terms.exitNoticeDays}-day exit period initiated.`
            : "Immediate exit request recorded and queued for administrative settlement.",
      });

      setExitDialogOpen(false);
      setExitReason("");
      fetchLifecycleAndTerms();
      if (onStateChange) onStateChange();
    } catch (err) {
      console.error("Exit request submission failed:", err);
      const msg = err.response?.data?.message || err.response?.data || "Unable to submit exit request.";
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: String(msg),
      });
    } finally {
      setSubmittingExit(false);
    }
  };

  // Handle Withdraw Notice Request
  const handleCancelNotice = async () => {
    if (!activeExitRequest?.id) return;
    const confirm = await Swal.fire({
      icon: "question",
      title: "Withdraw Exit Notice?",
      text: "This will cancel the pending exit and restore full regular operations.",
      showCancelButton: true,
      confirmButtonText: "Yes, Withdraw Notice",
    });

    if (!confirm.isConfirmed) return;

    try {


      Swal.fire({
        icon: "success",
        title: "Notice Withdrawn",
        text: "Exit notice has been cancelled. Company remains on Global Paybill rail.",
      });

      fetchLifecycleAndTerms();
      if (onStateChange) onStateChange();
    } catch (err) {
      console.error("Failed to cancel notice:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to withdraw exit notice at this time.",
      });
    }
  };

  // Handle Reactivate Company from Dormancy
  const handleReactivateCompany = async () => {
    setSubmittingReactivation(true);
    try {


      Swal.fire({
        icon: "success",
        title: "Company Reactivated",
        text: `Activation fee of ${formatKES(terms.activationFee)} debited. Platform operations restored.`,
      });

      setReactivateDialogOpen(false);
      fetchLifecycleAndTerms();
      if (onStateChange) onStateChange();
    } catch (err) {
      console.error("Reactivation failed:", err);
      const msg = err.response?.data?.message || err.response?.data || "Reactivation could not be processed.";
      Swal.fire({
        icon: "error",
        title: "Reactivation Failed",
        text: String(msg),
      });
    } finally {
      setSubmittingReactivation(false);
    }
  };

  const currentState = lifecycle.state;

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      {/* Header bar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1.2,
              borderRadius: 2,
              backgroundColor: "rgba(23, 102, 162, 0.12)",
              color: "#1766a2",
              display: "flex",
            }}
          >
            <LockResetOutlined fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100] }}>
              Company Lifecycle, Dormancy & Exit Management
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[400] }}>
              Single source of truth for platform operational permissions.
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            label={`State: ${currentState}`}
            color={
              currentState === "ACTIVE"
                ? "success"
                : currentState === "DORMANT"
                ? "error"
                : currentState === "NOTICE_PERIOD"
                ? "warning"
                : "default"
            }
            sx={{ fontWeight: 800 }}
          />
          <Tooltip title="Refresh Lifecycle Status">
            <IconButton
              onClick={fetchLifecycleAndTerms}
              size="small"
              sx={{
                border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                borderRadius: 1.5,
              }}
            >
              {loading ? <CircularProgress size={18} /> : <RefreshOutlined fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Dormancy Warning Banner */}
      {currentState === "DORMANT" && (
        <Alert
          severity="error"
          variant="filled"
          icon={<WarningAmberOutlined fontSize="inherit" />}
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => setReactivateDialogOpen(true)}
              sx={{ fontWeight: 700, borderColor: "#fff" }}
            >
              Pay Activation Fee & Reactivate
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Company Flagged as Dormant</AlertTitle>
          Disbursements and overdraft draws are temporarily blocked due to inactivity exceeding {terms.dormancyDays} days. Repayments continue to be accepted. Pay the {formatKES(terms.activationFee)} activation fee to resume.
        </Alert>
      )}

      {/* Notice Period Banner */}
      {currentState === "NOTICE_PERIOD" && (
        <Alert
          severity="warning"
          variant="filled"
          icon={<HourglassEmptyOutlined fontSize="inherit" />}
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleCancelNotice}
              sx={{ fontWeight: 700, borderColor: "#fff" }}
            >
              Withdraw Exit Notice
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 700 }}>30-Day Exit Notice In Progress</AlertTitle>
          Notice given on {lifecycle.noticeGivenAt ? new Date(lifecycle.noticeGivenAt).toLocaleDateString() : "-"}. Effective exit date: {lifecycle.exitEffectiveDate ? new Date(lifecycle.exitEffectiveDate).toLocaleDateString() : "Pending Settlement"}. No new overdraft draws permitted.
        </Alert>
      )}

      {/* Lifecycle Permissions State Table Card */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100], mb: 1 }}>
                State Machine Permissions.
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2.5 }}>
                Current operations allowed for your tenant based on platform status:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc" }}>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                      Disbursements
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: currentState === "ACTIVE" || currentState === "NOTICE_PERIOD" ? "#22bf72" : "#d32f2f", mt: 0.5 }}>
                      {currentState === "ACTIVE" || currentState === "NOTICE_PERIOD" ? "ALLOWED" : "BLOCKED"}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc" }}>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                      Overdraft Draws
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: currentState === "ACTIVE" ? "#22bf72" : "#d32f2f", mt: 0.5 }}>
                      {currentState === "ACTIVE" ? "ALLOWED" : "BLOCKED"}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc" }}>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                      Repayments
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#22bf72", mt: 0.5 }}>
                      ACTIVE
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc" }}>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                      Withdrawals
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: currentState !== "CLOSED" ? "#22bf72" : "#d32f2f", mt: 0.5 }}>
                      {currentState !== "CLOSED" ? "ALLOWED" : "BLOCKED"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2.5 }} />

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {currentState === "ACTIVE" && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<ExitToAppOutlined />}
                    onClick={() => setExitDialogOpen(true)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                  >
                    Initiate Platform Exit Request
                  </Button>
                )}

                {currentState === "DORMANT" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayCircleOutline />}
                    onClick={() => setReactivateDialogOpen(true)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                  >
                    Reactivate Company Now
                  </Button>
                )}

                {currentState === "NOTICE_PERIOD" && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CancelOutlined />}
                    onClick={handleCancelNotice}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                  >
                    Withdraw Exit Notice
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Commercial Terms Summary */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100], mb: 1 }}>
                Commercial Terms & Fees
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2 }}>
                Current contract parameters in database:
              </Typography>

              <Stack spacing={2}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                    Continuity Retained Reserve:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatKES(terms.minRetainedBalance)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                    Notice Exit Period:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {terms.exitNoticeDays} Days (No Fee)
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                    Immediate Early Exit Cost:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#d32f2f" }}>
                    {terms.earlyExitFeeType === "PERCENTAGE" ? `${terms.earlyExitFeeValue}% of balance` : formatKES(terms.earlyExitFeeValue)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                    Dormancy Inactivity Window:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {terms.dormancyDays} Days
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                    Company Activation Fee:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#22bf72" }}>
                    {formatKES(terms.activationFee)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Exit Request Dialog */}
      <Dialog open={exitDialogOpen} onClose={() => setExitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Initiate Platform Exit Request</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2.5 }}>
            Companies may choose between a standard notice period or immediate exit:
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend" sx={{ fontWeight: 700, color: colors.grey[200] }}>
              Choose Exit Strategy
            </FormLabel>
            <RadioGroup value={exitMode} onChange={(e) => setExitMode(e.target.value)}>
              <FormControlLabel
                value="NOTICE"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Exit with Notice ({terms.exitNoticeDays} days) - Recommended
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                      No early exit fee. Operations continue normally while migrating customers.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="IMMEDIATE"
                control={<Radio />}
                label={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#d32f2f" }}>
                      Immediate Exit (Incurs Operational Cost)
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                      Incurs {terms.earlyExitFeeType === "PERCENTAGE" ? `${terms.earlyExitFeeValue}%` : formatKES(terms.earlyExitFeeValue)} early exit fee. Disbursements stop immediately.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Reason for Exit"
            value={exitReason}
            onChange={(e) => setExitReason(e.target.value)}
            placeholder="e.g. Migrating to own dedicated Safaricom Paybill shortcode"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Negotiated Agreement Reference (Optional)"
            value={negotiatedRef}
            onChange={(e) => setNegotiatedRef(e.target.value)}
            placeholder="e.g. AGR-2026-017"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setExitDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitExit}
            disabled={submittingExit}
            startIcon={submittingExit ? <CircularProgress size={16} /> : <SendOutlined />}
            sx={{ fontWeight: 700 }}
          >
            Submit Exit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivation Dialog */}
      <Dialog open={reactivateDialogOpen} onClose={() => setReactivateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Reactivate Company Account</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: colors.grey[300], mb: 2 }}>
            To resume active disbursements and overdraft draws, the standard platform reactivation fee must be settled:
          </Typography>
          <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc", mb: 2 }}>
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              REQUIRED ACTIVATION FEE
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#22bf72", mt: 0.5 }}>
              {formatKES(terms.activationFee)}
            </Typography>
          </Paper>
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            This fee will be automatically debited from your available wallet balance upon confirmation (JE-16).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReactivateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleReactivateCompany}
            disabled={submittingReactivation}
            startIcon={submittingReactivation ? <CircularProgress size={16} /> : <CheckCircleOutline />}
            sx={{ fontWeight: 700 }}
          >
            Confirm & Reactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExitReactivation;
